import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import {
  defaultItemsStoreOptions,
  defaultItemsStoreQuery,
  defaultStoreOptions
} from '@/utils/pinia/defaults'
import { useApiClient } from '@/composables/api-client'
import { createQuery, createUrl } from '@/utils/pinia/helpers'
import type { PaginationMeta } from '~/types'
import type { ItemsStoreQuery, ItemsStoreSetupOptions } from './types'

const storeSetup = <
  K extends string,
  T extends { [key in K]: string | number }
>(
  defaultOptions: ItemsStoreSetupOptions<K>,
  defaultQuery: ItemsStoreQuery
) => {
  const items = ref<T[]>([]) as Ref<T[]>
  const meta = ref<PaginationMeta | null>(null)
  const url = ref('')
  const query = ref<ItemsStoreQuery | null>(null)
  const isLastPage = ref(true)

  const reset = () => {
    items.value = []
    query.value = null
    meta.value = null
    isLastPage.value = true
  }

  const fetchItems = async (
    requestQuery: ItemsStoreQuery = {},
    options: Record<string, any> = {}
  ) => {
    options = Object.assign({ resetBeforeFetch: true }, options)

    if (options.resetBeforeFetch) {
      reset()
    }

    const client = await useApiClient()
    const paginationQuery =
      defaultOptions.paginationType === 'page' ? { page: 1 } : {}
    const mergedQuery = Object.assign(
      {},
      defaultQuery,
      paginationQuery,
      requestQuery
    )
    const queryToRequest = createQuery(defaultOptions, mergedQuery)
    const urlToRequest = createUrl(defaultOptions.path, requestQuery, options)
    const requestOptions = Object.assign({}, { query: queryToRequest })

    const result = await client.get<
      | {
          items: T[]
          _meta: PaginationMeta
        }
      | T[]
      | null
    >(urlToRequest, requestOptions, {
      cache: defaultOptions.cache
    })

    if (defaultOptions.pagination && result && !Array.isArray(result)) {
      items.value = result.items
      meta.value = result._meta
      isLastPage.value =
        defaultOptions.paginationType === 'lastId'
          ? result._meta.lastId === null
          : result._meta.pageCount < 2
    } else {
      items.value = (result as T[] | null) ?? []
      isLastPage.value = true
    }

    url.value = urlToRequest
    query.value = mergedQuery

    return items.value
  }

  const fetchMoreItems = async () => {
    if (isLastPage.value || !query.value) {
      return
    }

    const client = await useApiClient()
    const lastId = items.value[items.value.length - 1][defaultOptions.idKey]
    const paginationQuery =
      defaultOptions.paginationType === 'page'
        ? { page: query.value.page + 1 }
        : { last_id: lastId }
    const mergedQuery = Object.assign({}, query.value, paginationQuery)
    const queryToRequest = createQuery(defaultOptions, mergedQuery)
    const requestOptions = Object.assign({}, { query: queryToRequest })

    const result = await client.get<{
      items: T[]
      _meta: PaginationMeta
    }>(url.value, requestOptions, {
      cache: defaultOptions.cache
    })

    items.value = items.value.concat(result.items)
    query.value = mergedQuery
    isLastPage.value =
      result._meta.totalCount === items.value.length ||
      (defaultOptions.paginationType === 'lastId'
        ? result._meta.lastId === null
        : result._meta.currentPage === result._meta.pageCount)

    if (defaultOptions.pagination) {
      meta.value = result._meta
    }

    return items.value
  }

  const updateItem = (id: T[K], updates: Partial<T>) => {
    const targetId = id.toString()
    const index = items.value.findIndex(
      item => item[defaultOptions.idKey].toString() === targetId
    )

    if (index > -1) {
      items.value[index] = Object.assign(items.value[index], updates)
    }
  }

  const addLeft = (data: T) => {
    items.value.unshift(data)
  }

  const addRight = (data: T) => {
    items.value.push(data)
  }

  const removeItem = (id: T[K]) => {
    const targetId = id.toString()
    const index = items.value.findIndex(
      item => item[defaultOptions.idKey].toString() === targetId
    )

    if (index > -1) {
      items.value.splice(index, 1)
    }

    if (meta.value && meta.value.totalCount) {
      meta.value.totalCount = Math.max(meta.value.totalCount - 1, 0)
    }
  }

  return {
    items,
    meta,
    url,
    query,
    isLastPage,
    reset,
    fetchItems,
    fetchMoreItems,
    updateItem,
    addLeft,
    addRight,
    removeItem
  }
}

export const createItemsStoreWrapper = <
  K extends string,
  T extends { [key in K]: string | number }
>() => {
  return <E extends Record<string, any> = {}>(
    key: string,
    options: ItemsStoreSetupOptions<K>,
    setup?: (context: ReturnType<typeof storeSetup<K, T>>) => E
  ) => {
    const storeOptions = Object.assign(
      {},
      defaultStoreOptions,
      defaultItemsStoreOptions,
      options
    )

    const defaultQuery = Object.assign(
      {},
      defaultItemsStoreQuery,
      options.query
    )

    return defineStore(key, () => {
      const context = storeSetup<K, T>(storeOptions, defaultQuery)

      const extending = Object.assign({}, setup?.(context))

      return { ...context, ...extending }
    })
  }
}
